'use client';

import React, { useState } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { twMerge } from 'tailwind-merge';
import { faRoute, faCheck, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import useRouteState from 'src/hooks/routes/useRouteState';
import useAddProviderToRoute from 'src/hooks/routes/useAddProviderToRoute';
import { ProviderResultItem } from 'src/components/Maps/ResultsList/ProviderResultItem';
import StrategyChainsCell from 'src/components/Table/StrategyChainsCell';
import { formatMetricValue } from 'src/utils/strategyHelpers';
import type { MetricStatSource } from 'src/utils/strategyHelpers';

const StrategyResultItem = ({
  provider,
  onSelect,
  setActiveMarker,
  chainsMetadata,
  isSelected,
  onToggleSelect,
  metricStat,
  isMetricSorted = false,
}: {
  provider: MedScout.Opportunity;
  onSelect: (provider: MedScout.Opportunity) => void;
  setActiveMarker: (markerId: string | null) => void;
  chainsMetadata: Record<string | number, { label: string }>;
  isSelected: boolean;
  onToggleSelect: (providerId: string, provider?: MedScout.Opportunity) => void;
  /** The metric shown as the row's stat; none when several chains are selected. */
  metricStat?: MetricStatSource | null;
  /** Whether the list is sorted by `metricStat`, which bolds its value. */
  isMetricSorted?: boolean;
}) => {

  const chainIds: number[] = (provider as any)?.chain_ids || [];

  // Route mode state
  const { isCreating, isEditing, tempStops } = useRouteState();
  const { addProviderToRoute, isLoading: isAddingToRoute } =
    useAddProviderToRoute();
  const isRouteMode = isCreating || isEditing;
  const isInRoute = tempStops.some((stop) => stop.providerId === String(provider.provider_id));
  const [addError, setAddError] = useState<string | null>(null);

  const metricValue = metricStat
    ? provider.metrics_v2?.[metricStat.metricId]?.[metricStat.code]
    : undefined;

  const handleSelectProvider = () => {
    onToggleSelect(provider.provider_id, provider);
  };

  const handleAddToRoute = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isInRoute || isAddingToRoute) return;

    setAddError(null);
    const success = await addProviderToRoute(provider.provider_id);
    if (!success) {
      setAddError('Failed to add provider to route');
    }
  };
  return (
    <ProviderResultItem
      data-testid={`strategy-result-item-${provider.provider_id}`}
      provider={provider}
      city={provider.city}
      state={provider.state}
      isSelected={isSelected}
      onToggleSelect={handleSelectProvider}
      onNameClick={() => onSelect(provider)}
      stats={
        metricStat
          ? [
              {
                key: metricStat.code,
                label: metricStat.label,
                value: metricValue,
                displayValue:
                  typeof metricValue === 'number'
                    ? formatMetricValue(
                        metricValue,
                        metricStat.type,
                        metricStat.code
                      )
                    : undefined,
                isActive: isMetricSorted,
              },
            ]
          : undefined
      }
      lists={provider.list_membership}
      crmEntityType={provider.type || provider.content_type}
      actions={
        isRouteMode && (
          isInRoute ? (
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center bg-emerald-100 text-emerald-600"
              title="Added to route"
              data-testid="in-route-indicator"
            >
              <Icon icon={faCheck} className="w-3.5 h-3.5" />
            </div>
          ) : (
            <button
              onClick={handleAddToRoute}
              disabled={isAddingToRoute}
              className="w-7 h-7 rounded-full flex items-center justify-center transition-colors bg-sky-100 text-sky-600 hover:bg-sky-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title={addError || 'Add to route'}
              aria-label="Add to route"
              data-testid="add-to-route-button"
            >
              <Icon
                icon={isAddingToRoute ? faSpinner : faRoute}
                className={twMerge(
                  'w-3.5 h-3.5',
                  isAddingToRoute && 'animate-spin'
                )}
              />
            </button>
          )
        )
      }
      onMouseEnter={() =>
        setActiveMarker(provider.provider_id?.toString() || null)
      }
      onMouseLeave={() => setActiveMarker(null)}
    >
      <StrategyChainsCell chainIds={chainIds} chainsMetadata={chainsMetadata} />
    </ProviderResultItem>
  );
};

export default StrategyResultItem;
